import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import { Upload, Loader2, X, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import { polygonsApi, getErrorMessage } from '../../lib/api';

const ACCEPTED_FORMATS = {
    'application/json': ['.geojson', '.json'],
    'application/zip': ['.zip'],
    'application/vnd.google-earth.kml+xml': ['.kml'],
    'application/vnd.google-earth.kmz': ['.kmz'],
    'application/gpx+xml': ['.gpx'],
    'application/geopackage+sqlite3': ['.gpkg'],
};

const ACCEPTED_EXTENSIONS_LABEL = '.geojson, .zip (Shapefile), .kml, .kmz, .gpx, .gpkg';

function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface UploadModalProps {
    onClose: () => void;
    onSuccess: () => void;
}

export default function UploadModal({ onClose, onSuccess }: UploadModalProps) {
    const { t } = useTranslation();
    const [uploading, setUploading] = useState(false);
    const [uploadCommodity, setUploadCommodity] = useState('cocoa');
    const [pendingFiles, setPendingFiles] = useState<File[]>([]);
    const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number; fileName: string } | null>(null);

    const onDrop = useCallback((acceptedFiles: File[]) => {
        setPendingFiles((prev) => [...prev, ...acceptedFiles]);
    }, []);

    const removeFile = (index: number) => {
        setPendingFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handleUpload = async () => {
        if (!pendingFiles.length) return;
        setUploading(true);
        let totalCreated = 0;
        let errorCount = 0;

        for (let i = 0; i < pendingFiles.length; i++) {
            const file = pendingFiles[i];
            setUploadProgress({ current: i + 1, total: pendingFiles.length, fileName: file.name });
            try {
                const result = await polygonsApi.uploadFile(file, uploadCommodity);
                totalCreated += result.created;
            } catch (error: any) {
                errorCount++;
                toast.error(`${file.name}: ${getErrorMessage(error) || 'Error'}`);
            }
        }

        if (totalCreated > 0) {
            toast.success(
                pendingFiles.length === 1
                    ? `${totalCreated} polígono(s) creado(s)`
                    : `${totalCreated} polígono(s) de ${pendingFiles.length} archivo(s)`
            );
        }

        setUploading(false);
        setUploadProgress(null);
        onClose();
        onSuccess();
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: ACCEPTED_FORMATS,
        disabled: uploading,
    });

    return (
        <div className="fixed inset-0 z-[1001] flex items-center justify-center p-4 bg-black/50">
            <div className="card p-8 w-full max-w-lg animate-slide-up">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    Upload geospatial file
                </h2>
                <p className="text-gray-500 mb-4">
                    Supports GeoJSON, Shapefile (ZIP), KML, KMZ, GPX, GeoPackage
                </p>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Commodity
                    </label>
                    <select
                        value={uploadCommodity}
                        onChange={(e) => setUploadCommodity(e.target.value)}
                        className="input"
                        disabled={uploading}
                    >
                        <option value="cocoa">Cocoa</option>
                        <option value="coffee">Coffee</option>
                        <option value="palm_oil">Palm Oil</option>
                        <option value="soy">Soy</option>
                        <option value="timber">Timber</option>
                        <option value="rubber">Rubber</option>
                        <option value="cattle">Cattle</option>
                    </select>
                </div>

                {/* Dropzone */}
                <div
                    {...getRootProps()}
                    className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-colors ${
                        uploading
                            ? 'pointer-events-none opacity-50 border-gray-200 dark:border-gray-700'
                            : isDragActive
                              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                              : 'border-gray-200 dark:border-gray-700 hover:border-primary-300'
                    }`}
                >
                    <input {...getInputProps()} />
                    <Upload className="w-10 h-10 mx-auto text-gray-400 mb-3" />
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                        {isDragActive
                            ? 'Drop files here...'
                            : pendingFiles.length > 0
                              ? 'Drop more files or click to add'
                              : 'Drag files or click to select'}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">{ACCEPTED_EXTENSIONS_LABEL}</p>
                </div>

                {/* File list */}
                {pendingFiles.length > 0 && (
                    <div className="mt-4 max-h-48 overflow-y-auto">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                            {pendingFiles.length} archivo{pendingFiles.length > 1 ? 's' : ''} seleccionado{pendingFiles.length > 1 ? 's' : ''}
                        </div>
                        <div className="space-y-1">
                            {pendingFiles.map((file, idx) => (
                                <div
                                    key={`${file.name}-${idx}`}
                                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800 text-sm"
                                >
                                    <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                    <span className="text-gray-700 dark:text-gray-300 truncate flex-1">
                                        {file.name}
                                    </span>
                                    <span className="text-xs text-gray-400 flex-shrink-0">
                                        {formatFileSize(file.size)}
                                    </span>
                                    {!uploading && (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); removeFile(idx); }}
                                            className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                                        >
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Upload progress */}
                {uploading && uploadProgress && (
                    <div className="mt-4">
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-2">
                            <Loader2 className="w-4 h-4 animate-spin text-primary-500" />
                            <span>
                                Subiendo {uploadProgress.current} de {uploadProgress.total}: {uploadProgress.fileName}
                            </span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                            <div
                                className="bg-primary-500 h-1.5 rounded-full transition-all duration-300"
                                style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                            />
                        </div>
                    </div>
                )}

                {/* Footer buttons */}
                <div className="flex justify-end gap-3 mt-6">
                    <button onClick={onClose} className="btn-secondary" disabled={uploading}>
                        {t('common.cancel')}
                    </button>
                    {pendingFiles.length > 0 && (
                        <button
                            onClick={handleUpload}
                            disabled={uploading}
                            className="btn-primary flex items-center gap-2"
                        >
                            {uploading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Upload className="w-4 h-4" />
                            )}
                            {uploading
                                ? 'Subiendo...'
                                : `Subir ${pendingFiles.length > 1 ? `(${pendingFiles.length} archivos)` : ''}`
                            }
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
